import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Star, Lock, Stethoscope } from 'lucide-react';
import LoginModal from '../admin/LoginModal';
import DoctorLoginModal from '../admin/DoctorLoginModal';

const Footer = () => {
  const { t } = useTranslation();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isDoctorLoginModalOpen, setIsDoctorLoginModalOpen] = useState(false);

  return (
    <>
      <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
      <DoctorLoginModal isOpen={isDoctorLoginModalOpen} onClose={() => setIsDoctorLoginModalOpen(false)} />
    <footer className="bg-gray-900 text-white pt-16 pb-8">
      <div className="container">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <svg 
                className="w-10 h-10" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <path d="M12 5.5c1.5-2 4-2.5 5.5-1.5 1.5 1 2 3.5 1 6.5 0 0 .5 6.5-6.5 9-7-2.5-6.5-9-6.5-9-1-3-.5-5.5 1-6.5 1.5-1 4-.5 5.5 1.5Z"/>
              </svg>
              <span className="text-2xl font-bold text-white">Clinic Dental Bahia</span>
            </div>
            <p className="text-gray-400 mb-4">
              Providing exceptional dental care with a compassionate touch, making every smile brighter.
            </p>
            <a 
              href="https://g.page/r/CVCTW2gfGNHJEBM/review"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-primary-400 hover:text-primary-300 transition-colors duration-300"
            >
              <Star size={20} />
              <span>Review us on Google</span>
            </a>
          </div>

          {/* Hours */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.hours.title')}</h3>
            <ul className="space-y-2">
              <li className="flex items-start">
                <Clock size={18} className="text-primary-400 mt-1 mr-2" />
                <span>Monday - Friday:<br />9:00 AM - 2:00 PM<br />4:00 PM - 8:00 PM</span>
              </li>
              <li className="flex items-start">
                <Clock size={18} className="text-primary-400 mt-1 mr-2" />
                <span>Saturday: Closed</span>
              </li>
              <li className="flex items-start">
                <Clock size={18} className="text-primary-400 mt-1 mr-2" />
                <span>Sunday: Closed</span>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.contact.title')}</h3>
            <ul className="space-y-2">
              <li className="flex items-start">
                <MapPin size={18} className="text-primary-400 mt-1 mr-2" />
                <span>Av, Dr Meca, 34 Discovery Building, 1-1a, Puerto de Mazarrón, Murcia, 30860</span>
              </li>
              <li className="flex items-start">
                <Phone size={18} className="text-primary-400 mt-1 mr-2" />
                <span>Clinic office: 968 59 50 16<br />WhatsApp: +34 692248724</span>
              </li>
              <li className="flex items-start">
                <Mail size={18} className="text-primary-400 mt-1 mr-2" />
                <span>{t('footer.contact.email')}</span>
              </li>
            </ul>
          </div>

          {/* Social */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.social.title')}</h3>
            <div className="flex space-x-4">
              <a 
                href="https://www.facebook.com/clinicdentalbahia" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-gray-800 hover:bg-primary-600 w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300"
              >
                <Facebook size={18} />
                <span className="sr-only">Facebook</span>
              </a>
              <a 
                href="https://www.instagram.com/clinica_dental_bahia/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-gray-800 hover:bg-primary-600 w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300"
              >
                <Instagram size={18} />
                <span className="sr-only">Instagram</span>
              </a>
              <a 
                href="https://g.page/r/CVCTW2gfGNHJEBM/review" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-gray-800 hover:bg-primary-600 w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300"
              >
                <Star size={18} />
                <span className="sr-only">Google Reviews</span>
              </a>
            </div>
            <div className="mt-6">
              <a 
                href="#appointment" 
                className="btn btn-primary"
              >
                {t('nav.book')}
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-500">
          <p>{t('footer.copyright')}</p>
          <div className="mt-4 flex items-center justify-center gap-6">
            <button
              onClick={() => setIsLoginModalOpen(true)}
              className="inline-flex items-center space-x-2 text-gray-600 hover:text-gray-400 transition-colors text-sm"
            >
              <Lock size={14} />
              <span>Admin</span>
            </button>
            <button
              onClick={() => setIsDoctorLoginModalOpen(true)}
              className="inline-flex items-center space-x-2 text-gray-600 hover:text-gray-400 transition-colors text-sm"
            >
              <Stethoscope size={14} />
              <span>Doctor Admin Login</span>
            </button>
          </div>
        </div>
      </div>
    </footer>
    </>
  );
};

export default Footer;